import { useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import { Link, useSearchParams } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { FilterPopover } from "@/components/ui/FilterPopover";
import { RadioFilter } from "@/components/ui/filters/RadioFilter";
import { CheckboxFilter } from "@/components/ui/filters/CheckboxFilter";
import {
  DateRangeFilter,
  DATE_PRESETS,
  resolveDatePreset,
  type DatePreset,
} from "@/components/ui/filters/DateRangeFilter";
import type { AdminGamesResult } from "@/types";

const ADMIN_GAMES_QUERY = gql`
  query AdminGames(
    $page: Int
    $perPage: Int
    $sortBy: String
    $sortDir: String
    $query: String
    $statusFilter: String
    $sourceFilter: String
    $bggFilter: String
    $createdFrom: String
    $createdTo: String
    $updatedFrom: String
    $updatedTo: String
    $descriptionEnSourceFilter: String
    $descriptionLtSourceFilter: String
    $expansionFilter: String
    $publicFilter: String
    $completedFrom: String
    $completedTo: String
    $approvalFilter: String
  ) {
    adminGames(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      sortDir: $sortDir
      query: $query
      statusFilter: $statusFilter
      sourceFilter: $sourceFilter
      bggFilter: $bggFilter
      createdFrom: $createdFrom
      createdTo: $createdTo
      updatedFrom: $updatedFrom
      updatedTo: $updatedTo
      descriptionEnSourceFilter: $descriptionEnSourceFilter
      descriptionLtSourceFilter: $descriptionLtSourceFilter
      expansionFilter: $expansionFilter
      publicFilter: $publicFilter
      completedFrom: $completedFrom
      completedTo: $completedTo
      approvalFilter: $approvalFilter
    ) {
      games {
        id
        title
        dataSource
        createdAt
        updatedAt
        hasCover
        hasEnDescription
        hasLtDescription
        bggId
        yearPublished
        completedAt
        approvalStatus
      }
      totalCount
      totalPages
      currentPage
    }
  }
`;

type SortField = "title" | "created_at" | "updated_at" | "completed_at" | "data_source";

const STATUS_OPTIONS = [
  { value: "missing_en_description", label: "Trūksta EN aprašymo" },
  { value: "missing_lt_description", label: "Trūksta LT aprašymo" },
  { value: "missing_cover", label: "Trūksta viršelio" },
  { value: "incomplete", label: "Trūksta bet kokios informacijos" },
  { value: "complete", label: "Pilna informacija" },
];

const BGG_OPTIONS = [
  { value: "", label: "Visi" },
  { value: "with_bgg", label: "Su BGG" },
  { value: "without_bgg", label: "Be BGG" },
];

const EXPANSION_OPTIONS = [
  { value: "", label: "Visi" },
  { value: "expansions", label: "Plėtiniai" },
  { value: "not_expansions", label: "Ne plėtiniai" },
];

const PUBLIC_OPTIONS = [
  { value: "", label: "Visi" },
  { value: "public", label: "Viešas" },
  { value: "not_public", label: "Neviešas" },
];

const APPROVAL_OPTIONS = [
  { value: "pending", label: "Laukia patvirtinimo" },
  { value: "approved", label: "Patvirtintas" },
  { value: "rejected", label: "Atmestas" },
];

const GAME_DATA_SOURCES_QUERY = gql`
  query GameDataSources {
    gameDataSources
  }
`;

const GAME_DESCRIPTION_SOURCES_QUERY = gql`
  query GameDescriptionSources {
    gameDescriptionSources {
      enSources
      ltSources
    }
  }
`;

function FunnelIcon({ active }: { active: boolean }) {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none">
      {active ? (
        <path
          d="M2 3h12l-4.5 5.5v4l-3 1.5V8.5L2 3z"
          fill="currentColor"
        />
      ) : (
        <path
          d="M2 3h12l-4.5 5.5v4l-3 1.5V8.5L2 3z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

type FilterColumn =
  | "source"
  | "bgg"
  | "created"
  | "updated"
  | "completed"
  | "status"
  | "approval"
  | null;

export function AdminGamesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState<FilterColumn>(null);
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Refs for filter anchors
  const sourceRef = useRef<HTMLTableCellElement>(null);
  const bggRef = useRef<HTMLTableCellElement>(null);
  const createdRef = useRef<HTMLTableCellElement>(null);
  const updatedRef = useRef<HTMLTableCellElement>(null);
  const completedRef = useRef<HTMLTableCellElement>(null);
  const statusRef = useRef<HTMLTableCellElement>(null);
  const approvalRef = useRef<HTMLTableCellElement>(null);

  const page = Number(searchParams.get("page")) || 1;
  const sortBy = (searchParams.get("sortBy") as SortField) || "created_at";
  const sortDir = (searchParams.get("sortDir") as "asc" | "desc") || "desc";
  const statusFilterRaw = searchParams.get("status") || "";
  const statusFilters = statusFilterRaw ? statusFilterRaw.split(",") : [];
  const sourceFilterRaw = searchParams.get("source") || "";
  const sourceFilters = sourceFilterRaw ? sourceFilterRaw.split(",") : [];
  const bggFilter = searchParams.get("bgg") || "";
  const approvalFilterRaw = searchParams.get("approval") || "";
  const approvalFilters = approvalFilterRaw ? approvalFilterRaw.split(",") : [];
  const searchQuery = searchParams.get("q") || "";

  // "More filters" state from URL
  const descEnSourceRaw = searchParams.get("descEnSource") || "";
  const descEnSourceFilters = descEnSourceRaw ? descEnSourceRaw.split(",") : [];
  const descLtSourceRaw = searchParams.get("descLtSource") || "";
  const descLtSourceFilters = descLtSourceRaw ? descLtSourceRaw.split(",") : [];
  const expansionFilter = searchParams.get("expansion") || "";
  const publicFilter = searchParams.get("public") || "";
  const completedPreset = (searchParams.get("completedPreset") as DatePreset) || null;
  const completedFrom = searchParams.get("completedFrom") || "";
  const completedTo = searchParams.get("completedTo") || "";

  // Date filter state from URL
  const createdPreset = (searchParams.get("createdPreset") as DatePreset) || null;
  const createdFrom = searchParams.get("createdFrom") || "";
  const createdTo = searchParams.get("createdTo") || "";
  const updatedPreset = (searchParams.get("updatedPreset") as DatePreset) || null;
  const updatedFrom = searchParams.get("updatedFrom") || "";
  const updatedTo = searchParams.get("updatedTo") || "";

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSort = (field: SortField) => {
    const newDir =
      sortBy === field
        ? sortDir === "asc"
          ? "desc"
          : "asc"
        : field === "title"
          ? "asc"
          : "desc";
    updateParams({ sortBy: field, sortDir: newDir, page: "" });
  };

  const handlePageChange = (newPage: number) => {
    updateParams({ page: newPage > 1 ? String(newPage) : "" });
  };

  const toggleFilter = (col: FilterColumn) => {
    setOpenFilter((prev) => (prev === col ? null : col));
  };

  // Resolve date ranges for GraphQL query
  const resolvedCreated = createdPreset
    ? resolveDatePreset(createdPreset)
    : { from: createdFrom, to: createdTo };
  const resolvedUpdated = updatedPreset
    ? resolveDatePreset(updatedPreset)
    : { from: updatedFrom, to: updatedTo };
  const resolvedCompleted = completedPreset
    ? resolveDatePreset(completedPreset)
    : { from: completedFrom, to: completedTo };

  const { data: sourcesData } = useQuery({
    queryKey: ["gameDataSources"],
    queryFn: () =>
      graphqlClient.request<{ gameDataSources: string[] }>(
        GAME_DATA_SOURCES_QUERY,
      ),
  });

  const sourceOptions = (sourcesData?.gameDataSources ?? []).map((s) => ({
    value: s,
    label: s,
  }));

  const { data: descSourcesData } = useQuery({
    queryKey: ["gameDescriptionSources"],
    queryFn: () =>
      graphqlClient.request<{
        gameDescriptionSources: { enSources: string[]; ltSources: string[] };
      }>(GAME_DESCRIPTION_SOURCES_QUERY),
  });

  const descEnSourceOptions = (
    descSourcesData?.gameDescriptionSources?.enSources ?? []
  ).map((s) => ({ value: s, label: s }));
  const descLtSourceOptions = (
    descSourcesData?.gameDescriptionSources?.ltSources ?? []
  ).map((s) => ({ value: s, label: s }));

  const { data, isLoading } = useQuery({
    queryKey: [
      "adminGames",
      page,
      searchQuery,
      sortBy,
      sortDir,
      statusFilterRaw,
      sourceFilterRaw,
      bggFilter,
      approvalFilterRaw,
      resolvedCreated.from,
      resolvedCreated.to,
      resolvedUpdated.from,
      resolvedUpdated.to,
      descEnSourceRaw,
      descLtSourceRaw,
      expansionFilter,
      publicFilter,
      resolvedCompleted.from,
      resolvedCompleted.to,
    ],
    queryFn: () =>
      graphqlClient.request<{ adminGames: AdminGamesResult }>(
        ADMIN_GAMES_QUERY,
        {
          page,
          perPage: 50,
          sortBy,
          sortDir,
          query: searchQuery || undefined,
          statusFilter: statusFilterRaw || undefined,
          sourceFilter: sourceFilterRaw || undefined,
          bggFilter: bggFilter || undefined,
          createdFrom: resolvedCreated.from || undefined,
          createdTo: resolvedCreated.to || undefined,
          updatedFrom: resolvedUpdated.from || undefined,
          updatedTo: resolvedUpdated.to || undefined,
          descriptionEnSourceFilter: descEnSourceRaw || undefined,
          descriptionLtSourceFilter: descLtSourceRaw || undefined,
          expansionFilter: expansionFilter || undefined,
          publicFilter: publicFilter || undefined,
          completedFrom: resolvedCompleted.from || undefined,
          completedTo: resolvedCompleted.to || undefined,
          approvalFilter: approvalFilterRaw || undefined,
        },
      ),
  });

  const result = data?.adminGames;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("lt-LT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if filters are active
  const isFilterActive = (col: FilterColumn) => {
    switch (col) {
      case "source":
        return sourceFilters.length > 0;
      case "bgg":
        return bggFilter !== "";
      case "created":
        return createdPreset !== null || createdFrom !== "" || createdTo !== "";
      case "updated":
        return updatedPreset !== null || updatedFrom !== "" || updatedTo !== "";
      case "completed":
        return completedPreset !== null || completedFrom !== "" || completedTo !== "";
      case "status":
        return statusFilters.length > 0;
      case "approval":
        return approvalFilters.length > 0;
      default:
        return false;
    }
  };

  const getActiveFilters = () => {
    const filters: { label: string; onRemove: () => void }[] = [];

    if (sourceFilters.length > 0) {
      filters.push({
        label: `Šaltinis: ${sourceFilters.join(", ")}`,
        onRemove: () => updateParams({ source: "", page: "" }),
      });
    }
    if (bggFilter) {
      const bggLabel = BGG_OPTIONS.find((o) => o.value === bggFilter)?.label || bggFilter;
      filters.push({
        label: `BGG: ${bggLabel}`,
        onRemove: () => updateParams({ bgg: "", page: "" }),
      });
    }
    if (createdPreset) {
      const presetLabel = DATE_PRESETS.find((p) => p.value === createdPreset)?.label || createdPreset;
      filters.push({
        label: `Sukurtas: ${presetLabel}`,
        onRemove: () => updateParams({ createdPreset: "", createdFrom: "", createdTo: "", page: "" }),
      });
    } else if (createdFrom || createdTo) {
      const parts = [createdFrom, createdTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Sukurtas: ${parts}`,
        onRemove: () => updateParams({ createdFrom: "", createdTo: "", page: "" }),
      });
    }
    if (updatedPreset) {
      const presetLabel = DATE_PRESETS.find((p) => p.value === updatedPreset)?.label || updatedPreset;
      filters.push({
        label: `Atnaujintas: ${presetLabel}`,
        onRemove: () => updateParams({ updatedPreset: "", updatedFrom: "", updatedTo: "", page: "" }),
      });
    } else if (updatedFrom || updatedTo) {
      const parts = [updatedFrom, updatedTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Atnaujintas: ${parts}`,
        onRemove: () => updateParams({ updatedFrom: "", updatedTo: "", page: "" }),
      });
    }
    if (completedPreset) {
      const presetLabel = DATE_PRESETS.find((p) => p.value === completedPreset)?.label || completedPreset;
      filters.push({
        label: `Pilna: ${presetLabel}`,
        onRemove: () => updateParams({ completedPreset: "", completedFrom: "", completedTo: "", page: "" }),
      });
    } else if (completedFrom || completedTo) {
      const parts = [completedFrom, completedTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Pilna: ${parts}`,
        onRemove: () => updateParams({ completedFrom: "", completedTo: "", page: "" }),
      });
    }
    if (statusFilters.length > 0) {
      const labels = statusFilters.map(
        (v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || v,
      );
      filters.push({
        label: `Būsena: ${labels.join(", ")}`,
        onRemove: () => updateParams({ status: "", page: "" }),
      });
    }
    if (descEnSourceFilters.length > 0) {
      filters.push({
        label: `EN aprašymo šaltinis: ${descEnSourceFilters.join(", ")}`,
        onRemove: () => updateParams({ descEnSource: "", page: "" }),
      });
    }
    if (descLtSourceFilters.length > 0) {
      filters.push({
        label: `LT aprašymo šaltinis: ${descLtSourceFilters.join(", ")}`,
        onRemove: () => updateParams({ descLtSource: "", page: "" }),
      });
    }
    if (expansionFilter) {
      const label = EXPANSION_OPTIONS.find((o) => o.value === expansionFilter)?.label || expansionFilter;
      filters.push({
        label: `Plėtinys: ${label}`,
        onRemove: () => updateParams({ expansion: "", page: "" }),
      });
    }
    if (publicFilter) {
      const label = PUBLIC_OPTIONS.find((o) => o.value === publicFilter)?.label || publicFilter;
      filters.push({
        label: `Viešumas: ${label}`,
        onRemove: () => updateParams({ public: "", page: "" }),
      });
    }
    if (approvalFilters.length > 0) {
      const labels = approvalFilters.map(
        (v) => APPROVAL_OPTIONS.find((o) => o.value === v)?.label || v,
      );
      filters.push({
        label: `Patvirtinimas: ${labels.join(", ")}`,
        onRemove: () => updateParams({ approval: "", page: "" }),
      });
    }

    return filters;
  };

  const activeFilters = getActiveFilters();

  const clearAllFilters = () => {
    updateParams({
      q: "", source: "", bgg: "", status: "", approval: "",
      createdPreset: "", createdFrom: "", createdTo: "",
      updatedPreset: "", updatedFrom: "", updatedTo: "",
      completedPreset: "", completedFrom: "", completedTo: "",
      descEnSource: "", descLtSource: "", expansion: "", public: "",
      page: "",
    });
  };

  const HeaderCell = ({
    field,
    label,
    filterCol,
    cellRef,
    children,
  }: {
    field?: SortField;
    label: string;
    filterCol: FilterColumn;
    cellRef: React.RefObject<HTMLTableCellElement | null>;
    children: React.ReactNode;
  }) => {
    const active = isFilterActive(filterCol);

    return (
      <th
        ref={cellRef}
        className="relative px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
      >
        <div className="inline-flex items-center gap-1.5">
          {field ? (
            <button
              className="inline-flex items-center gap-1 hover:text-gray-700"
              onClick={() => handleSort(field)}
            >
              {label}
              {sortBy === field && (
                <span className="text-primary-800">
                  {sortDir === "asc" ? "↑" : "↓"}
                </span>
              )}
            </button>
          ) : (
            <span>{label}</span>
          )}
          <button
            className={`rounded p-0.5 transition-colors ${
              active
                ? "text-primary-800 hover:text-primary-600"
                : "text-gray-300 hover:text-gray-500"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleFilter(filterCol);
            }}
          >
            <FunnelIcon active={active} />
          </button>
        </div>
        <FilterPopover
          open={openFilter === filterCol}
          onClose={() => setOpenFilter(null)}
          anchorRef={cellRef}
        >
          {children}
        </FilterPopover>
      </th>
    );
  };

  return (
    <div className="px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Admin valdymo skydas
      </Link>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Žaidimai</h1>
          {result && (
            <p className="mt-1 text-sm text-gray-500">
              Iš viso: {result.totalCount}
            </p>
          )}
        </div>
        <Link
          to="/admin/merge-games"
          className="rounded-lg border border-primary-300 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-primary-50"
        >
          Sujungimas
        </Link>
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <span
              key={filter.label}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-800"
            >
              {filter.label}
              <button
                onClick={filter.onRemove}
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          {activeFilters.length > 1 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Išvalyti visus
            </button>
          )}
        </div>
      )}

      <div className="mb-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateParams({ q: searchInput, page: "" });
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Ieškoti pagal pavadinimą..."
            className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="submit"
            className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Ieškoti
          </button>
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                updateParams({ q: "", page: "" });
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Išvalyti
            </button>
          )}
        </form>
      </div>

      <div className="mb-4">
        <button
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
        >
          Daugiau filtrų
          <svg
            className={`h-4 w-4 transition-transform ${showMoreFilters ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {showMoreFilters && (
          <div className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-white p-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                EN aprašymo šaltinis
              </label>
              <CheckboxFilter
                options={descEnSourceOptions}
                values={descEnSourceFilters}
                onChange={(vals) =>
                  updateParams({ descEnSource: vals.join(","), page: "" })
                }
                onClear={() => updateParams({ descEnSource: "", page: "" })}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                LT aprašymo šaltinis
              </label>
              <CheckboxFilter
                options={descLtSourceOptions}
                values={descLtSourceFilters}
                onChange={(vals) =>
                  updateParams({ descLtSource: vals.join(","), page: "" })
                }
                onClear={() => updateParams({ descLtSource: "", page: "" })}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                Plėtinys
              </label>
              <RadioFilter
                options={EXPANSION_OPTIONS}
                value={expansionFilter}
                onChange={(val) =>
                  updateParams({ expansion: val, page: "" })
                }
                onClear={() => updateParams({ expansion: "", page: "" })}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                Viešumas
              </label>
              <RadioFilter
                options={PUBLIC_OPTIONS}
                value={publicFilter}
                onChange={(val) =>
                  updateParams({ public: val, page: "" })
                }
                onClear={() => updateParams({ public: "", page: "" })}
              />
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : result && result.games.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <button
                      className="inline-flex items-center gap-1 hover:text-gray-700"
                      onClick={() => handleSort("title")}
                    >
                      Pavadinimas
                      {sortBy === "title" && (
                        <span className="text-primary-800">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>
                  <HeaderCell
                    field="data_source"
                    label="Šaltinis"
                    filterCol="source"
                    cellRef={sourceRef}
                  >
                    <CheckboxFilter
                      options={sourceOptions}
                      values={sourceFilters}
                      onChange={(vals) =>
                        updateParams({ source: vals.join(","), page: "" })
                      }
                      onClear={() => updateParams({ source: "", page: "" })}
                    />
                  </HeaderCell>
                  <HeaderCell
                    label="BGG"
                    filterCol="bgg"
                    cellRef={bggRef}
                  >
                    <RadioFilter
                      options={BGG_OPTIONS}
                      value={bggFilter}
                      onChange={(val) =>
                        updateParams({ bgg: val, page: "" })
                      }
                      onClear={() => updateParams({ bgg: "", page: "" })}
                    />
                  </HeaderCell>
                  <HeaderCell
                    label="Patvirtinimas"
                    filterCol="approval"
                    cellRef={approvalRef}
                  >
                    <CheckboxFilter
                      options={APPROVAL_OPTIONS}
                      values={approvalFilters}
                      onChange={(vals) =>
                        updateParams({ approval: vals.join(","), page: "" })
                      }
                      onClear={() => updateParams({ approval: "", page: "" })}
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="created_at"
                    label="Sukurtas"
                    filterCol="created"
                    cellRef={createdRef}
                  >
                    <DateRangeFilter
                      preset={createdPreset}
                      dateFrom={createdFrom}
                      dateTo={createdTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          createdPreset: preset || "",
                          createdFrom: df,
                          createdTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          createdPreset: "",
                          createdFrom: "",
                          createdTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="updated_at"
                    label="Atnaujintas"
                    filterCol="updated"
                    cellRef={updatedRef}
                  >
                    <DateRangeFilter
                      preset={updatedPreset}
                      dateFrom={updatedFrom}
                      dateTo={updatedTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          updatedPreset: preset || "",
                          updatedFrom: df,
                          updatedTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          updatedPreset: "",
                          updatedFrom: "",
                          updatedTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="completed_at"
                    label="Pilna"
                    filterCol="completed"
                    cellRef={completedRef}
                  >
                    <DateRangeFilter
                      preset={completedPreset}
                      dateFrom={completedFrom}
                      dateTo={completedTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          completedPreset: preset || "",
                          completedFrom: df,
                          completedTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          completedPreset: "",
                          completedFrom: "",
                          completedTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <HeaderCell
                    label="Būsena"
                    filterCol="status"
                    cellRef={statusRef}
                  >
                    <CheckboxFilter
                      options={STATUS_OPTIONS}
                      values={statusFilters}
                      onChange={(vals) =>
                        updateParams({ status: vals.join(","), page: "" })
                      }
                      onClear={() => updateParams({ status: "", page: "" })}
                    />
                  </HeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {result.games.map((game) => (
                  <tr key={game.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium">
                      <Link
                        to={`/admin/games/${game.id}`}
                        className="text-primary-800 hover:underline"
                      >
                        {game.title}
                      </Link>
                      {game.yearPublished && (
                        <span className="ml-1 text-gray-400">
                          ({game.yearPublished})
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {game.dataSource || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {game.bggId ? (
                        <a
                          href={`https://boardgamegeek.com/boardgame/${game.bggId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-800 hover:underline"
                        >
                          {game.bggId}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {game.approvalStatus === "approved" ? (
                        <Badge variant="success">Patvirtintas</Badge>
                      ) : game.approvalStatus === "rejected" ? (
                        <Badge variant="danger">Atmestas</Badge>
                      ) : (
                        <Badge variant="default">Laukia</Badge>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(game.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(game.updatedAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {game.completedAt ? formatDate(game.completedAt) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {game.hasCover &&
                        game.hasEnDescription &&
                        game.hasLtDescription ? (
                          <Badge variant="success">Pilna</Badge>
                        ) : (
                          <>
                            {!game.hasEnDescription && (
                              <Badge variant="danger">Nėra EN aprašymo</Badge>
                            )}
                            {!game.hasLtDescription && (
                              <Badge variant="danger">Nėra LT aprašymo</Badge>
                            )}
                            {!game.hasCover && (
                              <Badge variant="danger">Nėra viršelio</Badge>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={result.currentPage}
              totalPages={result.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Žaidimų nerasta</p>
        </div>
      )}
    </div>
  );
}
